import { dehydrate, QueryClient, useQuery } from "@tanstack/react-query"
import getAdvertisement from "api/sections/getAdvertisement"
import getSections from "api/sections/getSections"
import HomeAdsSlider from "components/HomeSlider/HomeAdsSlider"
import HomeHeroSlider from "components/HomeSlider/HomeHeroSlider"
import { MainLayout } from "components/Layout"
import AdSection from "components/Sections/AdSection/AdSection"
import AppBanner from "components/Sections/AppsSection/AppBanner"
import EventsSections from "components/Sections/EventsSections/EventsSections"
import { useLang } from "hooks/useLang"
import { GetServerSideProps } from "next"
import { useRouter } from "next/router"
import { NextSeo, OrganizationJsonLd, SoftwareAppJsonLd } from "next-seo"
import { useEffect, useMemo, useRef, useState } from "react"
import { IFinalEvent } from "types/event"
import {
    APPLE_APP_STORE_URL,
    GOOGLE_PLAY_STORE_URL,
} from "utils/helpers/constants"
import { COMPANY_INFO } from "../constants/company_info"
import { ArSEO, EnSEO } from "../seo/index"

interface HomePageProps {
    initialIsMobile: boolean
}

const EMPTY_ARRAY: any[] = []

const HomePage = ({ initialIsMobile }: HomePageProps) => {
    const { data: adsResponse } = useQuery({
        queryKey: ["advertisements"],
        queryFn: getAdvertisement,
    })

    const advertisement = adsResponse?.payload || EMPTY_ARRAY

    const advertisementSliders = advertisement?.filter(
        (ad) => ad.web_view_type === "slider"
    )
    const advertisementGrid = advertisement?.filter(
        (ad) => ad.web_view_type === "grid"
    )

    const { data: sectionsDataResponse, isLoading } = useQuery({
        queryKey: ["sections"],
        queryFn: getSections,
    })

    const sectionsData = sectionsDataResponse?.payload.sections || EMPTY_ARRAY

    const allEvents = useMemo(() => {
        const events =
            sectionsData?.flatMap(
                (section) => section.events || section.videos
            ) || EMPTY_ARRAY
        const seen = new Set<string>()
        return events.filter((event) => {
            if (seen.has(event._id)) return false
            seen.add(event._id)
            return true
        })
    }, [sectionsData])

    const [filteredEvents, setFilteredEvents] =
        useState<IFinalEvent[]>(allEvents)

    // const videos_sections: ISections[] =
    //   sectionsData?.filter(
    //     (section) =>
    //       section.type === "videos" &&
    //       Array.isArray(section.videos) &&
    //       section.videos.length > 0
    //   ) || [];

    const filterOptions = [
        { label: "All", value: "all" },
        { label: "Today", value: "today" },
        { label: "This_week", value: "week" },
        { label: "This_month", value: "month" },
    ]

    // Sync filtered events with all events - use direct assignment instead of effect
    useEffect(() => {
        if (allEvents.length > 0) {
            setFilteredEvents(allEvents)
        }
    }, [allEvents])

    const heroRef = useRef<HTMLDivElement | null>(null)

    const router = useRouter()

    const { isAr } = useLang()
    const [currentSlide, setCurrentSlide] = useState(0)

    return (
        <>
            {(() => {
                const baseUrl = (
                    process.env.NEXT_PUBLIC_SITE_URL ||
                    "https://pass-tickets.com"
                ).replace(/\/$/, "")
                const currentPath =
                    (router.asPath || "/").split("#")[0].split("?")[0] || "/"
                const isEn =
                    /^\/en-US(\/?|$)/.test(currentPath) ||
                    router.locale === "en-US"
                const canonicalUrl = `${baseUrl}${currentPath}`
                const pathNoLocale = currentPath.replace(
                    /^\/(en-US|ar-SA)(?=\/|$)/,
                    ""
                )
                const defaultHref = `${baseUrl}${pathNoLocale || "/"}`
                const enHref = `${baseUrl}/en-US${pathNoLocale || "/"}`
                return (
                    <NextSeo
                        {...(isEn ? EnSEO : ArSEO)}
                        canonical={canonicalUrl}
                        languageAlternates={[
                            { hrefLang: "x-default", href: defaultHref },
                            { hrefLang: "ar", href: defaultHref },
                            { hrefLang: "en", href: enHref },
                        ]}
                        openGraph={{ url: canonicalUrl }}
                    />
                )
            })()}

            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebSite",
                        url: "https://pass-tickets.com",
                        potentialAction: {
                            "@type": "SearchAction",
                            target: "https://pass-tickets.com/search?q={search_term_string}",
                            "query-input": "required name=search_term_string",
                        },
                    }),
                }}
            />

            {(() => {
                const baseUrl = (
                    process.env.NEXT_PUBLIC_SITE_URL ||
                    "https://pass-tickets.com"
                ).replace(/\/$/, "")
                const social = COMPANY_INFO?.social_media || ({} as any)
                const sameAs = Object.values(social).filter(
                    (v) =>
                        typeof v === "string" &&
                        /^https?:\/\//.test(v as string)
                ) as string[]
                return (
                    <OrganizationJsonLd
                        useAppDir={false}
                        type="Organization"
                        name="PassTickets"
                        url={baseUrl}
                        logo={`${baseUrl}/iconx/android-chrome-512x512.png`}
                        sameAs={sameAs}
                        address={{ addressCountry: "SA" }}
                        areaServed={["SA"]}
                    />
                )
            })()}

            {/* SoftwareApplication JSON-LD for mobile apps */}
            {(() => {
                const currentPath =
                    (router.asPath || "/").split("#")[0].split("?")[0] || "/"
                const isEn =
                    /^\/en-US(\/?|$)/.test(currentPath) ||
                    router.locale === "en-US"
                const appName = isEn ? "PassTickets" : "باس تيكيتس"
                return (
                    <>
                        <SoftwareAppJsonLd
                            useAppDir={false}
                            type="MobileApplication"
                            name={appName}
                            price="0"
                            priceCurrency="USD"
                            applicationCategory="EntertainmentApplication"
                            operatingSystem="Android"
                            offers={[
                                {
                                    "@type": "Offer",
                                    price: "0",
                                    priceCurrency: "USD",
                                    url: GOOGLE_PLAY_STORE_URL,
                                },
                            ]}
                        />
                        <SoftwareAppJsonLd
                            useAppDir={false}
                            type="MobileApplication"
                            name={appName}
                            price="0"
                            priceCurrency="USD"
                            applicationCategory="EntertainmentApplication"
                            operatingSystem="iOS"
                            offers={[
                                {
                                    "@type": "Offer",
                                    price: "0",
                                    priceCurrency: "USD",
                                    url: APPLE_APP_STORE_URL,
                                },
                            ]}
                        />
                    </>
                )
            })()}

            <MainLayout>
                <div
                    ref={heroRef}
                    className="fixed left-0 top-0 -z-10 h-full w-full overflow-hidden bg-[#211b52] md:block md:aspect-video md:max-h-screen"
                >
                    <HomeHeroSlider
                        advertisements={advertisementSliders!}
                        currentIndex={currentSlide}
                    />
                </div>

                {/* Content overlays the fixed blurred hero background */}
                <div className="relative flex flex-col">
                    {/* <DiscoverSection isLoading={false} /> */}

                    <div>
                        <HomeAdsSlider
                            currentIndex={currentSlide}
                            onSlideChange={setCurrentSlide}
                            ads={advertisement}
                        />

                        {/* <div className="hidden md:flex max-w-[1180px] mx-auto mt-10  md:px-0">
              <EventFilteration
                setFilteredEvents={setFilteredEvents}
                options={filterOptions}
                data={allEvents}
                filterFunction={(event: IFinalEvent, filter: string) =>
                  filterEventsByDate(
                    filter as FilterOption,
                    event.event_date.starting_date,
                    event.event_date.ending_date,
                    event.time_slots
                  )
                }
              />
            </div> */}

                        {/* <div className="mt-4 layout">
              <div className="flex md:hidden">
                <EventFilteratioMobile
                  setFilteredEvents={setFilteredEvents}
                  options={filterOptions}
                  data={allEvents}
                  filterFunction={(event: IFinalEvent, filter: string) =>
                    filterEventsByDate(
                      filter as FilterOption,
                      event.event_date.starting_date,
                      event.event_date.ending_date,
                      event.time_slots
                    )
                  }
                />
              </div>
            </div> */}

                        <EventsSections
                            sectionsData={sectionsData}
                            //videos_sections={videos_sections}
                            filteredEvents={filteredEvents}
                            isLoading={isLoading}
                            initialIsMobile={initialIsMobile}
                        />

                        {advertisement?.length === 0 ? null : (
                            <AdSection
                                advertisements={advertisementGrid!}
                                isLoading={false}
                            />
                        )}
                    </div>
                    <AppBanner />
                </div>
            </MainLayout>
        </>
    )
}

export default HomePage

// SSR: Render on every request for fresh data
export const getServerSideProps: GetServerSideProps = async (context) => {
    const queryClient = new QueryClient()
    const userAgent = (context.req?.headers["user-agent"] || "") as string
    const initialIsMobile = /Mobi|Android|iPhone|iPad|iPod|Mobile/i.test(
        userAgent
    )

    try {
        // Prefetch both queries in parallel
        await Promise.all([
            queryClient.prefetchQuery({
                queryKey: ["advertisements"],
                queryFn: getAdvertisement,
            }),
            queryClient.prefetchQuery({
                queryKey: ["sections"],
                queryFn: getSections,
            }),
        ])
    } catch (error) {
        console.error("Error prefetching homepage data:", error)
    }

    return {
        props: {
            dehydratedState: dehydrate(queryClient),
            initialIsMobile,
        },
    }
}
